import React from 'react';
import {
  ExIconButton, IconButtonFlavor, IconButtonSize, IconButtonType,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import '../PreviewSection.scss';
import DisplayProfilePicture from '@components/AgentBuilderWizard/DisplayProfilePicture/DisplayProfilePicture';

const formatJsonForPreview = (jsonString: string, maxLength: number = 200): string => {
  if (!jsonString || jsonString === '{}') return '';

  try {
    const parsed = JSON.parse(jsonString);
    const formatted = JSON.stringify(parsed, null, 2);

    if (formatted.length <= maxLength) {
      return formatted;
    }

    const lines = formatted.split('\n');
    let preview = '';
    let currentLength = 0;

    for (let i = 0; i < lines.length; i += 1) {
      const line = lines[i];
      if (currentLength + line.length + 1 > maxLength - 3) {
        preview += '...';
        break;
      }
      preview += `${line}\n`;
      currentLength += line.length + 1;
    }

    return preview.trim();
  } catch {
    return jsonString.length > maxLength
      ? `${jsonString.substring(0, maxLength)}...`
      : jsonString;
  }
};

function ConversationalModeSection({ template, t }: any) {
  const hasPersonalityTraits = template.personality_traits
    && Object.keys(template.personality_traits).length > 0;
  const voiceTone = hasPersonalityTraits
    ? Object.entries(template.personality_traits).find(([key]) => key === 'voice_tone')?.[1]
    : null;

  return (
    <>
      <div className="info-row">
        <div className="info-key">{t('agent_mode')}</div>
        <div className="info-value">{t('profile_agent_mode_conversational')}</div>
      </div>

      <div className="info-row">
        <div className="info-key">{t('personality')}</div>
        <div className="info-value">
          <ul>
            {hasPersonalityTraits && (
              Object.entries(template.personality_traits).filter(([key]) => key !== 'voice_tone').map(
                ([key, value]: [string, any]) => (
                  <li key={key}>
                    {key}
                    :
                    {' '}
                    {value}
                  </li>
                ),
              )
            )}
          </ul>
        </div>
      </div>

      <div className="info-row">
        <div className="info-key">{t('profile_tone_title')}</div>
        <div className="info-value">{voiceTone || t('friendly')}</div>
      </div>

      <div className="info-row">
        <div className="info-key">{t('conversation_starter')}</div>
        <div className="info-value">
          {template?.conversation_starters?.length > 0 ? (
            <ul>
              {template.conversation_starters.map((conv: any) => (
                <li key={conv.value}>{conv}</li>
              ))}
            </ul>
          ) : (
            <span>{'\u2013'}</span>
          )}
        </div>
      </div>
    </>
  );
}

function StructuredModeSection({ template }: any) {
  return (
    <>
      <div className="info-row">
        <div className="info-key">Agent Mode</div>
        <div className="info-value">Structured</div>
      </div>
      <div className="info-row">
        <div className="info-key">Input Schema</div>
        <div className="info-value">
          {template?.input_json_schema && template.input_json_schema !== '{}' ? (
            <div className="schema-preview">
              <pre className="json-preview">{formatJsonForPreview(template.input_json_schema, 300)}</pre>
            </div>
          ) : (
            '\u2013'
          )}
        </div>
      </div>
      <div className="info-row">
        <div className="info-key">Output Schema</div>
        <div className="info-value">
          {template?.output_json_schema && template.output_json_schema !== '{}' ? (
            <div className="schema-preview">
              <pre className="json-preview">{formatJsonForPreview(template.output_json_schema, 300)}</pre>
            </div>
          ) : (
            '\u2013'
          )}
        </div>
      </div>
    </>
  );
}

function ProfileSection({ template, handleStepClick, showEdit }: any) {
  const [t] = useTranslation('common');
  const processingMode = template?.inference_configuration?.performance_config?.processing_mode === 'quick'
    ? t('Fast')
    : t('Standard');
  const hasRationale = template?.inference_configuration?.performance_config?.rationale;
  const fileUploadAllowed = template?.inference_configuration?.file_inference_config?.allowed;
  const fileTypes = template?.inference_configuration?.file_inference_config?.types;

  return (
    <div className="review-agent-container">
      {showEdit && (
        <div className="preview-button-container">
          <div className="agent-title">{t('review_panel_profile_title')}</div>
          <ExIconButton
            icon="pencil"
            type={IconButtonType.TERTIARY}
            flavor={IconButtonFlavor.BASE}
            size={IconButtonSize.SMALL}
            tooltipText="Edit"
            onClick={(e: any) => {
              handleStepClick(e, 'Profile');
            }}
          />
        </div>
      )}
      <div className="review-agent-section">
        <div className="info-row">
          <div className="info-key">{t('goal')}</div>
          <div className="info-value">
            {template?.objective?.length
              ? <span style={{ whiteSpace: 'pre-line' }}>{template?.objective}</span> : '\u2013'}
          </div>
        </div>
        <div className="info-row">
          <div className="info-key">{t('agent_photo')}</div>
          <div className="info-value">
            <div className="picture-icon-container">
              <DisplayProfilePicture
                selectedAvatar={
                  template?.profile_picture?.avatar || template?.profile_picture?.role
                }
                selectedColor={
                  template?.profile_picture?.color || template?.profile_picture?.colour
                }
                iconSize="M"
              />
            </div>
          </div>
        </div>
        <div className="info-row">
          <div className="info-key">{t('ai_agent_garden_agent_name')}</div>
          <div className="info-value">{template?.name?.length ? template?.name : '\u2013'}</div>
        </div>

        {template?.agent_mode !== 'structured' && <ConversationalModeSection template={template} t={t} />}
        {template?.agent_mode === 'structured' && <StructuredModeSection template={template} />}

        <div className="info-row">
          <div className="info-key">{t('profile_model_config_title')}</div>
          <div className="info-value">
            {t('model')}
            :
            {' '}
            {processingMode}
            <br />
            {t('extended_thinking')}
            :
            {' '}
            {hasRationale ? t('on') : t('off')}
          </div>
        </div>
        <div className="info-row">
          <div className="info-key">File Upload</div>
          <div className="info-value">
            {fileUploadAllowed ? 'On' : 'Off'}
            {fileUploadAllowed && fileTypes && fileTypes.length > 0 && (
              <>
                <br />
                {t('profile_allowed_formats')}
                :
                {' '}
                {fileTypes.join(', ')}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default ProfileSection;
